import React, { useCallback, useState } from "react";
import { connect } from "react-redux";

import { faVideo } from "@fortawesome/pro-solid-svg-icons/faVideo";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIntl } from "react-intl";

import IntegrationService from "@mapmycustomers/shared/enum/integrations/IntegrationService";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button } from "@mapmycustomers/ui";

import GecodeLimitInfo from "@app/component/GecodeLimitInfo";
import { isOnTrialPlan } from "@app/store/iam";
import { selectService } from "@app/store/integrations/actions";
import { getCurrentIntegration } from "@app/store/integrations/selectors";
import SUPPORTED_SERVICES from "@app/store/integrations/supported-services";
import { RootState } from "@app/store/rootReducer";
import Integration from "@app/types/integrations/Integration";
import useModalVisibilityAnalyticEffect from "@app/util/hook/useModalVisibilityAnalyticEffect";
import getContactWithIntegrationTeamLink from "@app/util/integrations/getContactWithIntegrationTeamLink";

import SettingPage from "../common/SettingPage";

import Links from "./components/Links";
import ServiceButtons from "./components/ServiceButtons";
import TwoWaySyncAlert from "./components/TwoWaySyncAlert";
import VideoTutorialModal from "./components/VideoTutorialModal";
import WizardModal from "./components/WizardModal";
import ZapierBlock from "./components/ZapierBlock";
import integrationAnalytics from "./integrationAnalytics";

interface Props {
  currentIntegration?: Partial<Integration>;
  isTrial?: boolean;
  selectService: typeof selectService;
}

const NoSyncIntegration: React.FC<Props> = ({ isTrial, selectService }) => {
  const intl = useIntl();

  const [videoTutorialVisible, showVideoTutorialModal, hideVideoTutorialModal] = useBoolean();
  const [wizardModalVisible, showWizardModal, hideWizardModal] = useBoolean();
  const [choicedService, chooseService] = useState<IntegrationService | undefined>();

  useModalVisibilityAnalyticEffect("Video Tutorial", videoTutorialVisible, integrationAnalytics);

  const handleChangeService = useCallback(
    (service: IntegrationService | undefined) => {
      chooseService(service);
      showWizardModal();
      if (service) {
        const foundService = SUPPORTED_SERVICES.find(({ type }) => type === service);
        if (foundService) {
          integrationAnalytics.clicked([
            "Add New Integration",
            foundService?.shortName ?? foundService.name,
          ]);
        }
      }
    },
    [showWizardModal, chooseService]
  );

  const handleSelect = useCallback(
    (isSandbox?: boolean) => {
      if (choicedService) {
        selectService({ integrationService: choicedService, isSandbox });
      }
    },
    [choicedService, selectService]
  );

  return (
    <SettingPage
      actions={
        <Button
          leftIcon={<FontAwesomeIcon icon={faVideo} />}
          onClick={showVideoTutorialModal}
          type="primary"
        >
          {intl.formatMessage({
            id: "integrations.add.showVideoTutorialModal",
            defaultMessage: "Video Tutorial",
            description: "Text of video tutorial button",
          })}
        </Button>
      }
      subTitle={
        <div>
          <div>
            <GecodeLimitInfo
              noLimitReachedTitle={intl.formatMessage({
                id: "integrations.home.noIntegration.geocodingLimit.title",
                defaultMessage: "Moving large amounts of new data with your integration?",
                description: "Integration home page - no limit title",
              })}
            />
          </div>
          <div>
            {intl.formatMessage(
              {
                id: "integrations.add.header.subTitle",
                defaultMessage:
                  "Select your CRM to start automatically moving data into Map My Customers. By default, integrations sync data in one direction from your CRM into Map My Customers. For custom logic and two-way syncing, <a>please contact our integration team</a>.",
                description: "Text of add integration header subtitle",
              },
              {
                a: (text) => (
                  <a
                    href={getContactWithIntegrationTeamLink(isTrial ?? false)}
                    rel="noreferrer"
                    target="_blank"
                  >
                    {text}
                  </a>
                ),
              }
            )}
          </div>
        </div>
      }
      title={intl.formatMessage({
        id: "integrations.add.header.title",
        defaultMessage: "Integrations",
        description: "Text of add integration header",
      })}
    >
      <ServiceButtons onChange={handleChangeService} />
      <TwoWaySyncAlert />
      <ZapierBlock />
      <Links />
      <VideoTutorialModal onHide={hideVideoTutorialModal} visible={videoTutorialVisible} />
      <WizardModal
        onHide={hideWizardModal}
        onSelectService={handleSelect}
        selectedService={choicedService}
        visible={wizardModalVisible}
      />
    </SettingPage>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentIntegration: getCurrentIntegration(state),
  isTrial: isOnTrialPlan(state),
});

const mapDispatchToProps = {
  selectService,
};

export default connect(mapStateToProps, mapDispatchToProps)(NoSyncIntegration);
